/**
 * HomeRedirect Component (Issue #695, #822, #2323)
 *
 * Redirects /home to user's primary club with club-first navigation state.
 * Part of club-first navigation architecture.
 *
 * Flow:
 * 1. Get user's primary club slug from usePrimaryClub()
 * 2. Redirect to /clubs/:slug with { fromHomeTab: true } state
 * 3. If no clubs, show ZeroClubWelcome (#2323 — invite-first dual hero;
 *    replaced the AppHomePage fallback, whose Browse-Clubs hero pushed
 *    discover-first)
 */

import { Navigate } from 'react-router-dom';
import { usePrimaryClub } from '../hooks/usePrimaryClub';
import { useClubs } from '../hooks/useClubs';
import Button from './Button';
import TopBar from './TopBar';
import ZeroClubWelcome from './ZeroClubWelcome';
import { OroLoader } from './OroLoader';

export default function HomeRedirect() {
  const primaryClubSlug = usePrimaryClub();
  // usePrimaryClub maps BOTH "clubs still loading/errored" and "genuinely
  // zero clubs" to null (usePrimaryClub.ts). Welcome is an onboarding
  // screen — showing it to an existing member during the cold-load window
  // (or on a failed fetch) misclassifies them (#2331 review P2). Same
  // ['clubs'] query key, so this dedupes with the hook's own fetch.
  const { data: clubs, isLoading, isError, refetch } = useClubs();

  if (!primaryClubSlug) {
    // Both interim states keep the TopBar: on md+ the BottomNav is hidden,
    // so a chrome-less persistent error strands the user with no path to
    // Settings/sign-out — the same failure round 1 fixed on ZeroClubWelcome
    // (#2331 review round 3). pt-14 offsets the fixed h-14 bar.
    if (isLoading || (!clubs && !isError)) {
      return (
        <>
          <TopBar title="Home" />
          <div className="pt-14 flex justify-center py-16" data-testid="home-redirect-loading">
            <OroLoader label="Loading" />
          </div>
        </>
      );
    }
    if (isError) {
      return (
        <>
          <TopBar title="Home" />
          <div className="pt-14 px-4 py-16 max-w-md mx-auto text-center space-y-4">
            <p className="text-text-secondary">Couldn&apos;t load your clubs.</p>
            <Button variant="secondary" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        </>
      );
    }
    // Clubs query resolved successfully to an empty list: genuinely
    // zero clubs → invite-first Welcome (join via invite / start a club;
    // discover demoted to a tertiary link — #2323)
    return <ZeroClubWelcome />;
  }

  // Redirect to primary club with Home tab state marker
  return (
    <Navigate
      to={`/clubs/${primaryClubSlug}`}
      state={{ fromHomeTab: true }}
      replace
    />
  );
}
